import { createSlice } from '@reduxjs/toolkit';

/**
 * Reports slice — the report catalogue, the org's scheduled reports, and the ad-hoc download.
 *
 * Shapes follow docs/domains/reports/api-contract.md:
 *   types   → GET /reports/types   (static catalogue: id, name, description, fields[])
 *   list    → GET /reports         (scheduled reports; DISABLED ones are returned too)
 *   areas   → GET /deviceCategories?defaultCategoryID=…  (what `scope.areaIDs` may contain)
 *
 * 🛑 `downloading` is a real in-flight flag, not cosmetic: `/reports/download` generates
 * SYNCHRONOUSLY and can take seconds, and its 500 ("above the 5000 limit — narrow the scope")
 * is the user's instruction, not a generic failure. The wizard therefore stays open and
 * disabled during a download instead of closing optimistically the way a mutation modal does.
 */
const initialState = {
	// Catalogue — org-independent, so it survives a profile switch (see extraReducers).
	types: [],
	typesLoading: false,
	typesLoaded: false,

	// Scheduled reports for the active org.
	list: [],
	listLoading: false,
	listLoaded: false,

	// The scopeable "areas" of a locker report — DEVICE CATEGORIES, org-scoped. See the saga:
	// `scope.areaIDs` carries categoryIDs, so this list is what the wizard's picker offers.
	areas: [],
	areasLoading: false,
	areasLoaded: false,

	creating: false,
	updating: false,
	deleting: false,
	downloading: false,

	error: null
};

const reportsSlice = createSlice({
	name: 'reports',
	initialState,
	reducers: {
		// ── Catalogue ───────────────────────────────────────────────────
		loadReportTypes: (state) => {
			state.typesLoading = true;
			state.error = null;
		},
		loadReportTypesSuccess: (state, action) => {
			state.typesLoading = false;
			state.typesLoaded = true;
			state.types = action.payload || [];
		},
		loadReportTypesError: (state, action) => {
			state.typesLoading = false;
			state.error = action.payload;
		},

		// ── Scope areas (device categories) ─────────────────────────────
		loadReportAreas: (state) => {
			state.areasLoading = true;
			state.error = null;
		},
		loadReportAreasSuccess: (state, action) => {
			state.areasLoading = false;
			state.areasLoaded = true;
			state.areas = action.payload || [];
		},
		loadReportAreasError: (state, action) => {
			state.areasLoading = false;
			// Dropped rather than kept: a stale category list would offer the operator a scope the
			// server will reject as foreign, and the wizard's empty state says so honestly.
			state.areas = [];
			state.error = action.payload;
		},

		// ── Scheduled reports ───────────────────────────────────────────
		loadReports: (state) => {
			state.listLoading = true;
			state.error = null;
		},
		loadReportsSuccess: (state, action) => {
			state.listLoading = false;
			state.listLoaded = true;
			state.list = action.payload || [];
		},
		loadReportsError: (state, action) => {
			state.listLoading = false;
			// The list is dropped, not kept: these rows say who is receiving org data on a
			// schedule. Stale rows presented as current is the wrong answer to that question.
			state.list = [];
			state.error = action.payload;
		},

		createReport: (state) => {
			state.creating = true;
			state.error = null;
		},
		createReportSuccess: (state) => {
			state.creating = false;
		},
		createReportError: (state, action) => {
			state.creating = false;
			state.error = action.payload;
		},

		// payload: { reportID, patch }
		updateReport: (state) => {
			state.updating = true;
			state.error = null;
		},
		updateReportSuccess: (state) => {
			state.updating = false;
		},
		updateReportError: (state, action) => {
			state.updating = false;
			state.error = action.payload;
		},

		// payload: reportID
		deleteReport: (state) => {
			state.deleting = true;
			state.error = null;
		},
		deleteReportSuccess: (state) => {
			state.deleting = false;
		},
		deleteReportError: (state, action) => {
			state.deleting = false;
			state.error = action.payload;
		},

		// ── Ad-hoc download ─────────────────────────────────────────────
		// payload: { reportTypeID, format, scope, filename }
		downloadReport: (state) => {
			state.downloading = true;
			state.error = null;
		},
		downloadReportSuccess: (state) => {
			state.downloading = false;
		},
		downloadReportError: (state, action) => {
			state.downloading = false;
			// Held so the wizard can show the server's own sentence (the row-limit 500 names the
			// count and tells the user what to do) next to the button they just pressed.
			state.error = action.payload;
		},
		clearReportError: (state) => {
			state.error = null;
		}
	},
	extraReducers: (builder) => {
		builder.addCase('auth/logout', () => initialState);
		// Reports are org-scoped and NOTHING in the UI changes when the profile switches — without
		// this the previous org's schedules and recipients stay on screen under the new org's name.
		// Keyed on switchProfile*Success* (auth/saga.js has a real error path): resetting on the
		// attempt would blank the list after a FAILED switch, with no orgID change to refetch on.
		//
		// The catalogue is deliberately kept — `GET /reports/types` is static and org-independent,
		// so re-fetching it on every switch is a request that can only return the same answer.
		builder.addCase('auth/switchProfileSuccess', (state) => ({
			...initialState,
			types: state.types,
			typesLoaded: state.typesLoaded
		}));
	}
});

export const reportsActions = reportsSlice.actions;

export default reportsSlice.reducer;
